import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import PrinterStatusIndicator from './PrinterStatusIndicator';
import logo from '../assets/logo.png';

const BarraLateral = () => {
    const location = useLocation();
    const { user, logout } = useContext(AuthContext);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 992);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [isControlClinicoOpen, setIsControlClinicoOpen] = useState(false);
    const [updateAvailable, setUpdateAvailable] = useState(false);

    useEffect(() => {
        let initialEtag = null;
        let initialLastModified = null;

        const checkForUpdates = async () => {
            try {
                const response = await fetch('/?t=' + new Date().getTime(), { method: 'HEAD' });
                const etag = response.headers.get('etag');
                const lastModified = response.headers.get('last-modified');

                if (!initialEtag && !initialLastModified) {
                    initialEtag = etag;
                    initialLastModified = lastModified;
                } else {
                    if ((etag && etag !== initialEtag) || (lastModified && lastModified !== initialLastModified)) {
                        setUpdateAvailable(true);
                    }
                }
            } catch (error) {
                console.error('Error checking for updates:', error);
            }
        };

        checkForUpdates();
        const interval = setInterval(checkForUpdates, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 1024;
            setIsMobile(mobile);
            if (window.innerWidth <= 992) {
                setIsCollapsed(true);
            } else {
                setIsCollapsed(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path) ? 'active' : '';
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const menuItems = [
        { path: '/', icon: 'bi-house-door-fill', label: 'Inicio' },
        { path: '/clinica/agenda', icon: 'bi-calendar-date-fill', label: 'Agenda / Citas' },
        { path: '/clinica/consultas', icon: 'bi-clipboard2-pulse-fill', label: 'Consultas' },
        { path: '/clinica/registro-mensual', icon: 'bi-journal-bookmark-fill', label: 'Registro Mensual' },
        { path: '/clinica/pacientes', icon: 'bi-person-badge-fill', label: 'Pacientes' },
        { path: '/clinica/certificado-visual', icon: 'bi-file-earmark-medical-fill', label: 'Certificado Visual' },
        { path: '/clinica/servicios', icon: 'bi-bandaid-fill', label: 'Servicios' },
        { path: '/clinica/procedimientos', icon: 'bi-file-medical', label: 'Procedimientos' },
        { path: '/clinica/bodega', icon: 'bi-eyeglasses', label: 'Inventario (Armazones)' },
        { path: '/clinica/graficos-consentimiento', icon: 'bi-images', label: 'Gráficos Consentimiento' },
        { path: '/clinica/gastos', icon: 'bi-wallet2', label: 'Gastos' },
        { path: '/clinica/facturacion', icon: 'bi-receipt-cutoff', label: 'Facturas' },
        { path: '/clinica/recordatorios', icon: 'bi-envelope-paper-fill', label: 'Recordatorios por correo' },
        { path: '/clinica/printers', icon: 'bi-printer-fill', label: 'Impresoras' },

        { path: '/clinica/clinical-terms', icon: 'bi-book-half', label: 'Diccionario Clínico' },
        { path: '/users', icon: 'bi-people-fill', label: 'Usuarios' },
    ];

    // Estilos inline
    const styles = {
        sidebar: {
            position: 'fixed',
            top: 0,
            left: isMobile ? (isCollapsed ? '-250px' : '0') : '0',
            height: '100vh',
            width: isMobile ? '250px' : (isCollapsed ? '70px' : '250px'),
            background: 'var(--sidebar-bg)',
            color: 'var(--sidebar-text)',
            transition: 'all 0.3s ease',
            // Debajo de cualquier modal (1040+): antes estaba en 10000 y tapaba el lado izquierdo de los modales.
            zIndex: 1030,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.05)',
            overflowX: 'hidden',
            borderRight: '1px solid var(--border-color)'
        },
        sidebarHeader: {
            padding: '1.5rem 1rem',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
        },
        brandContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem'
        },
        brandLogo: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minWidth: 0,
            padding: '10px 0'
        },
        brandIcon: {
            display: 'none'
        },
        brandText: {
            display: 'none'
        },
        btnToggle: {
            background: 'var(--sidebar-bg)',
            border: '1px solid var(--border-color)',
            color: 'var(--sidebar-text)',
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            fontSize: '1.1rem',
            flexShrink: 0,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        },
        userInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem',
            background: 'var(--sidebar-bg)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            opacity: isCollapsed ? 0 : 1,
            height: isCollapsed ? 0 : 'auto',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
        },
        userAvatar: {
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#f3f4f6',
            color: '#111827',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            fontWeight: '600',
            flexShrink: 0
        },
        userDetails: {
            flex: 1,
            minWidth: 0
        },
        userName: {
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--sidebar-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        userRole: {
            fontSize: '0.75rem',
            color: '#6b7280',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        sidebarNav: {
            listStyle: 'none',
            padding: '1rem 0',
            margin: 0,
            flex: 1,
            overflowY: 'auto'
        },
        navItem: {
            margin: '0.25rem 0'
        },
        navLink: {
            display: 'flex',
            alignItems: 'center',
            padding: '0.875rem 1.25rem',
            color: 'var(--sidebar-text)',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
            gap: '1rem',
            position: 'relative',
            borderRadius: '8px',
            margin: '0 0.5rem',
            opacity: 0.85
        },
        navLinkActive: {
            background: '#111827',
            color: '#ffffff',
            fontWeight: '600',
            opacity: 1
        },
        navIcon: {
            fontSize: '1.25rem',
            minWidth: '24px',
            transition: 'all 0.2s'
        },
        navText: {
            whiteSpace: 'nowrap',
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : 'auto',
            overflow: 'hidden',
            transition: 'opacity 0.2s'
        },
        sidebarFooter: {
            padding: '1rem',
            borderTop: '1px solid var(--border-color)'
        },
        logoutButton: {
            width: '100%',
            padding: '0.75rem 1rem',
            background: 'transparent',
            border: '1.5px solid #ef4444',
            borderRadius: '8px',
            color: '#ef4444',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: '0.75rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            transition: 'all 0.2s ease'
        },
        versionText: {
            textAlign: 'center',
            fontSize: '0.7rem',
            color: '#64748b',
            marginTop: '0.5rem',
            opacity: isCollapsed ? 0 : 1,
            height: isCollapsed ? 0 : 'auto',
            overflow: 'hidden',
            transition: 'all 0.2s'
        }
    };

    return (
        <>
            {/* Agregar Bootstrap Icons */}
            <link
                rel="stylesheet"
                href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css"
            />

            {/* Mobile overlay */}
            {isMobile && !isCollapsed && (
                <div 
                    onClick={() => setIsCollapsed(true)} 
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1029 }}
                />
            )}
            
            {/* Floating Hamburger for Mobile */}
            {isMobile && isCollapsed && (
                <button 
                    onClick={toggleSidebar}
                    style={{
                        position: 'fixed', top: '15px', left: '15px', zIndex: 1031,
                        background: '#111827', color: '#fff', border: 'none',
                        width: '40px', height: '40px', borderRadius: '8px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)', cursor: 'pointer', fontSize: '1.5rem'
                    }}
                >
                    <i className="bi bi-list"></i>
                </button>
            )}

            <aside style={styles.sidebar}>
                {/* Header con Logo y Toggle */}
                <div style={styles.sidebarHeader}>
                    <div style={styles.brandContainer}>
                        <div style={styles.brandLogo}>
                            <img src={logo} alt="Vision-Click" style={{ height: isCollapsed ? '40px' : '120px', transition: 'all 0.3s', objectFit: 'contain', maxWidth: '100%' }} />
                        </div>
                        <button
                            style={styles.btnToggle}
                            onClick={toggleSidebar}
                            title={isCollapsed ? 'Expandir' : 'Contraer'}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#f1f5f9';
                                e.target.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#f8fafc';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
                        </button>
                    </div>

                    {/* Info del Usuario */}
                    <div style={styles.userInfo}>
                        <div style={styles.userAvatar}>
                            {user?.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div style={styles.userDetails}>
                            <div style={styles.userName}>
                                {user?.username || 'Usuario'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <ul style={styles.sidebarNav}>
                    {(() => {
                        const roleName = user?.role_details?.name;
                        const isSuperAdmin = roleName === 'SUPER_ADMIN' || user?.is_superuser;
                        
                        const filteredMenuItems = menuItems.filter(item => {
                            if (isSuperAdmin) return true;
                            
                            if (roleName === 'DOCTOR') {
                                const doctorPaths = ['/clinica/agenda', '/clinica/pacientes', '/clinica/certificado-visual', '/clinica/consultas', '/clinica/facturacion', '/clinica/servicios', '/clinica/graficos-consentimiento'];
                                return doctorPaths.includes(item.path);
                            }
                            
                            if (roleName === 'RECEPCIONISTA') {
                                const recepcionistaPaths = ['/clinica/agenda', '/clinica/pacientes', '/clinica/certificado-visual', '/clinica/registro-mensual', '/clinica/servicios', '/clinica/printers', '/clinica/recordatorios', '/clinica/facturacion'];
                                return recepcionistaPaths.includes(item.path);
                            }
                            
                            if (roleName === 'AYUDANTE') {
                                const ayudantePaths = ['/clinica/agenda', '/clinica/pacientes', '/clinica/certificado-visual', '/clinica/consultas', '/clinica/facturacion', '/clinica/bodega'];
                                return ayudantePaths.includes(item.path);
                            }

                            return false;
                        });

                        const topItems = [];
                        const controlClinicoItems = [];
                        
                        const order = ['/clinica/agenda', '/clinica/consultas', '/clinica/pacientes', '/clinica/certificado-visual', '/clinica/facturacion', '/clinica/bodega'];
                        
                        filteredMenuItems.forEach(item => {
                            if (order.includes(item.path)) {
                                topItems.push(item);
                            } else {
                                controlClinicoItems.push(item);
                            }
                        });
                        
                        topItems.sort((a, b) => order.indexOf(a.path) - order.indexOf(b.path));

                        const renderNavItem = (item, isSubItem = false) => {
                            const active = isActive(item.path);
                            return (
                                <li key={item.path} style={styles.navItem}>
                                    <Link
                                        to={item.path}
                                        title={isCollapsed && !isSubItem ? item.label : ''}
                                        style={{
                                            ...styles.navLink,
                                            ...(active ? styles.navLinkActive : {}),
                                            paddingLeft: isSubItem ? (isCollapsed ? '1.25rem' : '2.5rem') : '1.25rem',
                                            fontSize: isSubItem ? '0.9rem' : '1rem'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!active) {
                                                e.currentTarget.style.background = '#f3f4f6';
                                                e.currentTarget.style.color = '#111827';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!active) {
                                                e.currentTarget.style.background = 'transparent';
                                                e.currentTarget.style.color = 'var(--sidebar-text)';
                                            }
                                        }}
                                    >
                                        <i
                                            className={`bi ${item.icon}`}
                                            style={{
                                                ...styles.navIcon,
                                                color: active ? '#ffffff' : 'var(--sidebar-text)',
                                                fontSize: isSubItem ? '1.1rem' : '1.25rem'
                                            }}
                                        ></i>
                                        <span style={styles.navText}>{item.label}</span>
                                    </Link>
                                </li>
                            );
                        };

                        return (
                            <>
                                {topItems.map(item => renderNavItem(item))}
                                
                                {controlClinicoItems.length > 0 && (
                                    <li style={styles.navItem}>
                                        <div
                                            onClick={() => {
                                                if(isCollapsed) setIsCollapsed(false);
                                                setIsControlClinicoOpen(!isControlClinicoOpen);
                                            }}
                                            style={{
                                                ...styles.navLink,
                                                cursor: 'pointer',
                                                justifyContent: 'space-between',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'var(--hover-bg, rgba(0,0,0,0.05))';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'transparent';
                                            }}
                                            title={isCollapsed ? 'Control Clínico' : ''}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                                <i className="bi bi-gear-fill" style={{ ...styles.navIcon, color: 'var(--sidebar-text)' }}></i>
                                                <span style={styles.navText}>Control Clínico</span>
                                            </div>
                                            {!isCollapsed && (
                                                <i className={`bi bi-chevron-${isControlClinicoOpen ? 'up' : 'down'}`} style={{ transition: 'all 0.3s' }}></i>
                                            )}
                                        </div>
                                        
                                        <ul style={{ 
                                            listStyle: 'none', 
                                            padding: 0, 
                                            margin: 0, 
                                            maxHeight: isControlClinicoOpen && !isCollapsed ? '1000px' : '0', 
                                            overflow: 'hidden', 
                                            transition: 'max-height 0.3s ease-in-out' 
                                        }}>
                                            {controlClinicoItems.map(item => renderNavItem(item, true))}
                                        </ul>
                                    </li>
                                )}
                            </>
                        );
                    })()}
                </ul>

                {/* Footer con Logout */}
                <div style={styles.sidebarFooter}>
                    {!isCollapsed && <PrinterStatusIndicator />}
                    
                    <button
                        style={{
                            ...styles.logoutButton,
                            border: updateAvailable ? '1.5px solid #10b981' : '1.5px solid #d1d5db',
                            color: updateAvailable ? '#10b981' : '#4b5563',
                            background: 'transparent',
                            marginTop: isCollapsed ? '0.5rem' : '1rem',
                            marginBottom: '0.5rem',
                            position: 'relative'
                        }}
                        onClick={() => {
                            if ('caches' in window) {
                                caches.keys().then((names) => {
                                    names.forEach(name => caches.delete(name));
                                });
                            }
                            if ('serviceWorker' in navigator) {
                                navigator.serviceWorker.getRegistrations().then(function(registrations) {
                                    for(let registration of registrations) {
                                        registration.unregister();
                                    }
                                });
                            }
                            setTimeout(() => {
                                window.location.reload(true);
                            }, 500);
                        }}
                        title={updateAvailable ? "¡Actualización Disponible!" : "Descargar Actualizaciones"}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = updateAvailable ? '#ecfdf5' : '#f3f4f6';
                            e.currentTarget.style.borderColor = updateAvailable ? '#059669' : '#9ca3af';
                            e.currentTarget.style.color = updateAvailable ? '#059669' : '#111827';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = updateAvailable ? '#10b981' : '#d1d5db';
                            e.currentTarget.style.color = updateAvailable ? '#10b981' : '#4b5563';
                        }}
                    >
                        <i className="bi bi-arrow-clockwise" style={{ fontSize: '1.1rem' }}></i>
                        <span style={styles.navText}>
                            {updateAvailable ? '¡Actualización Lista!' : 'Actualizar Sistema'}
                        </span>
                        {updateAvailable && (
                            <span style={{
                                position: 'absolute', top: '5px', right: '5px',
                                width: '10px', height: '10px', background: '#ef4444',
                                borderRadius: '50%', boxShadow: '0 0 0 2px var(--sidebar-bg)'
                            }}></span>
                        )}
                    </button>

                    <button
                        style={{
                            ...styles.logoutButton,
                            marginTop: isCollapsed ? '0.5rem' : '1rem'
                        }}
                        onClick={logout}
                        title="Cerrar Sesión"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.borderColor = '#dc2626';
                            e.currentTarget.style.color = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#ef4444';
                            e.currentTarget.style.color = '#ef4444';
                        }}
                    >
                        <i className="bi bi-box-arrow-right" style={{ fontSize: '1.1rem' }}></i>
                        <span style={styles.navText}>Cerrar Sesión</span>
                    </button>

                    <div style={styles.versionText}>
                        v1.0.0
                    </div>
                </div>
            </aside>

            {/* Estilos globales adicionales */}
            <style>{`
                /* Scrollbar personalizado */
                .sidebar-nav::-webkit-scrollbar {
                    width: 4px;
                }
                .sidebar-nav::-webkit-scrollbar-track {
                    background: transparent;
                }
                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 4px;
                }
                .sidebar-nav::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }

                /* Ajuste para el contenido principal */
                .main-content {
                    margin-left: ${isMobile ? '0' : (isCollapsed ? '70px' : '250px')} !important;
                    transition: margin-left 0.3s ease;
                    min-height: 100vh;
                }
            `}</style>
        </>
    );
};

export default BarraLateral;